import { array, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { useCallback } from 'react';
import { Option } from '@mews-commander/types';
import { createOption } from '@mews-commander/ui';
import { useLocalizeEnum, useLocalizeTextOrNull } from '@mews/framework';

export const useHintText = () => {
    const localizeTextOrNull = useLocalizeTextOrNull();

    return useCallback(
        (prefix: string, value: string, hintModifier?: string) =>
            localizeTextOrNull({
                textKey: `${prefix}${value}${hintModifier ?? ''}Hint` as Mews.TextKey,
            }),
        [localizeTextOrNull]
    );
};

export const orderByLabel = pipe(
    string.Ord,
    ord.contramap(({ label }: Pick<Option, 'label'>) => (typeof label === 'string' ? label.toLocaleLowerCase() : ''))
);

export const useLocalizeOption = (omitHint = false) => {
    const createHintText = useHintText();
    const localizeEnum = useLocalizeEnum() as (prefix: string, value: string) => string;

    return useCallback(
        (prefix: string, value: string, hintModifier?: string) => ({
            label: localizeEnum(prefix, value),
            hint: !omitHint ? createHintText(prefix, value, hintModifier) : null,
        }),
        [createHintText, localizeEnum, omitHint]
    );
};

export const useCreateSelectFieldOptions = <T extends string>(
    enabledValues: T[],
    disabledValues: T[],
    enumPrefix: string,
    omitHint = false,
    hintModifier?: string
) => {
    const localizeOption = useLocalizeOption(omitHint);

    return flow(
        (values: T[]) => (enabledValues.length ? values.filter((value) => enabledValues.includes(value)) : values),
        array.map((value) => {
            const { label, hint } = localizeOption(enumPrefix, value, hintModifier);
            return createOption({
                disabled: disabledValues?.includes(value),
                label,
                secondaryText: hint,
                value,
            });
        })
    );
};
